import { ApiClient } from '@core/apiClient';

export interface CompliancePage {
  id: number;
  framework_id: number;
  framework_version_id?: number;
  template_node_id?: number;
  clause_ref?: string | null;
  source_ref?: string | null;
  section: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'approved_for_audit' | 'certified' | 'not_started';
  assigned_to: number;
  company_id: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  reviewed_at?: string;
  certified_at?: string;
  certified_by?: number;
  due_date?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  framework_name?: string;
  // Compliance intent fields (populated from template_nodes)
  evidence_required?: boolean;
  evidence_type?: 'document' | 'number' | 'text' | 'mixed';
  assessor_only_notes?: string;
  guidance?: string;
  internal_comments?: string;
  audit_findings?: string;
}

export interface EvidenceItem {
  id: number;
  compliance_page_id: number;
  uploaded_by: number;
  company_id: number;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  checksum: string;
  notes?: string;
  status: 'draft' | 'submitted' | 'INTERNAL_VERIFIED' | 'REJECTED' | 'expired';
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  expires_at?: string | null;
  first_name?: string;
  last_name?: string;
}

export interface CompanyCompliancePage extends CompliancePage {
  assigned_to_name: string;
  assigned_to_email?: string;
  framework_name: string;
  evidence_count?: number;
  evidence_verified?: number;
  evidence_rejected?: number;
  evidence_submitted?: number;
}

export class ComplianceExecutionApi extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  /**
   * Get assigned compliance pages for the current user
   */
  async getAssignedPages(): Promise<CompliancePage[]> {
    const response = await this.client.get('/compliance/execution/pages');
    return response.data.data;
  }

  /**
   * Get a specific compliance page.
   *
   * Tries the owner-scoped endpoint first (/pages/:id requires assigned_to = currentUser).
   * If that 404s (e.g. an internal auditor viewing a page they didn't author), falls back
   * to the review endpoint which accepts any caller with `compliance.review` permission.
   */
  async getCompliancePage(id: number): Promise<CompliancePage> {
    try {
      const response = await this.client.get(`/compliance/execution/pages/${id}`);
      return response.data.data;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 403) {
        // Fall back to the auditor/reviewer endpoint
        const reviewResp = await this.client.get(`/compliance/execution/company/pages/${id}/review`);
        return reviewResp.data.data;
      }
      throw err;
    }
  }

  /**
   * Submit a compliance page for review
   */
  async submitPage(id: number): Promise<CompliancePage> {
    const response = await this.client.post(`/compliance/execution/pages/${id}/submit`);
    return response.data.data;
  }

  /**
   * Get evidence items for a compliance page
   */
  async getEvidenceItems(pageId: number): Promise<EvidenceItem[]> {
    const response = await this.client.get(`/compliance/execution/pages/${pageId}/evidence`);
    return response.data.data;
  }

  /**
   * Get external auditor pages (Blind Audit)
   */
  async getExternalAuditorPages(): Promise<CompliancePage[]> {
    const response = await this.client.get('/compliance/execution/system/pages');
    return response.data.data;
  }

  /**
   * Get a single compliance page with full detail (evidence, template fields, audit trail)
   * for external auditor review.
   */
  async getExternalAuditorPageDetail(pageId: number): Promise<any> {
    const response = await this.client.get(`/compliance/execution/system/pages/${pageId}/review`);
    return response.data.data;
  }

  /**
   * Issue a framework-level compliance certificate (External Auditor action).
   * Called once all pages for the framework are marked certified.
   */
  async issueCertification(input: { companyId: number; frameworkId: number; frameworkVersionId: number; auditReportUrl?: string }): Promise<any> {
    const response = await this.client.post('/compliance/certifications/issue', {
      companyId: input.companyId,
      frameworkId: input.frameworkId,
      frameworkVersionId: input.frameworkVersionId,
      certificationType: 'initial',
      auditReportUrl: input.auditReportUrl,
    });
    return response.data.data;
  }

  /**
   * Request changes on a compliance page (External Auditor)
   */
  async requestChangesExternal(pageId: number, comments: string): Promise<boolean> {
    const response = await this.client.post(`/compliance/execution/system/pages/${pageId}/request-changes`, { comments });
    return response.data.success === true;
  }

  /**
   * Certify a compliance page (External Auditor only)
   */
  async certifyPage(pageId: number, comments: string): Promise<boolean> {
    const response = await this.client.post(`/compliance/execution/system/pages/${pageId}/certify`, { comments });
    return response.data.success === true;
  }

  /**
   * Upload evidence for a compliance page
   */
  async uploadEvidence(compliancePageId: number, file: File, opts?: { notes?: string; expiresAt?: string | null }): Promise<EvidenceItem> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('compliance_page_id', compliancePageId.toString());
    if (opts?.notes) {
      formData.append('notes', opts.notes);
    }
    if (opts?.expiresAt) {
      formData.append('expires_at', opts.expiresAt);
    }

    const response = await this.client.post('/compliance/execution/evidence', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async updateEvidenceExpiry(evidenceId: number, expiresAt: string | null): Promise<EvidenceItem> {
    const response = await this.client.patch(`/compliance/execution/evidence/${evidenceId}/expiry`, { expires_at: expiresAt });
    return response.data.data;
  }

  /**
   * Delete evidence item
   */
  async deleteEvidence(id: number): Promise<void> {
    await this.client.delete(`/compliance/execution/evidence/${id}`);
  }

  /**
   * Submit evidence item
   */
  async submitEvidence(id: number): Promise<EvidenceItem> {
    const response = await this.client.post(`/compliance/execution/evidence/${id}/submit`);
    return response.data.data;
  }

  /**
   * Download evidence file
   */
  async downloadEvidence(id: number): Promise<Blob> {
    const response = await this.client.get(`/compliance/execution/evidence/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Company Admin Methods

  /**
   * Get all compliance pages for the company (Company Admin)
   */
  async getCompanyCompliancePages(): Promise<CompanyCompliancePage[]> {
    const response = await this.client.get('/compliance/execution/company/pages');
    return response.data.data;
  }

  /**
   * Assign compliance pages to a user (Company Admin)
   */
  async assignCompliancePages(pageIds: number[], assignedToUserId: number, dueDate?: string | null): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/compliance/execution/company/pages/assign', {
      page_ids: pageIds,
      assigned_to_user_id: assignedToUserId,
      due_date: dueDate ?? null,
    });
    return response.data;
  }

  /**
   * Unassign compliance pages (Company Admin)
   */
  async unassignCompliancePages(pageIds: number[]): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/compliance/execution/company/pages/unassign', {
      page_ids: pageIds
    });
    return response.data;
  }

  /**
   * Get company frameworks (Company Admin)
   */
  async getCompanyFrameworks(): Promise<any[]> {
    const response = await this.client.get('/compliance/execution/company/frameworks');
    return response.data.data;
  }

  /**
   * Get company evidence (Company Admin)
   */
  async getCompanyEvidence(): Promise<any[]> {
    const response = await this.client.get('/compliance/execution/company/evidence');
    return response.data.data;
  }

  /**
   * Approve evidence (Internal Auditor)
   */
  async approveEvidence(evidenceId: number, notes?: string): Promise<EvidenceItem> {
    const response = await this.client.post(`/compliance/execution/evidence/${evidenceId}/approve`, { notes });
    return response.data.data;
  }

  /**
   * Reject evidence (Internal Auditor)
   */
  async rejectEvidence(evidenceId: number, reason: string): Promise<EvidenceItem> {
    const response = await this.client.post(`/compliance/execution/evidence/${evidenceId}/reject`, { reason });
    return response.data.data;
  }

  /**
   * Submit framework for external audit (Internal Auditor)
   */
  async submitForExternalAudit(frameworkId: number): Promise<any> {
    const response = await this.client.post(`/compliance/execution/internal-audit/submit/${frameworkId}`);
    return response.data.data;
  }
}

export const complianceExecutionApi = new ComplianceExecutionApi();
